import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Toaster, toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  FileText,
  Users,
  Building2,
  Settings,
  LogOut,
  Menu,
  X,
  Home,
  ChevronRight,
  Copy,
  ExternalLink,
  Sparkles,
  Smile
} from 'lucide-react';

// API URL
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sidebar Navigation
const SidebarNav = ({ isOpen, setIsOpen }) => {
  const { user, logout, isAdmin, canUseDentalChart } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { icon: LayoutDashboard, label: 'Overview', path: '/dashboard' },
    { icon: Sparkles, label: 'AI Estimate', path: '/dashboard/ai-estimate', isNew: true },
    { icon: FileText, label: 'Cost Estimates', path: '/dashboard/estimates' },
    { icon: Users, label: 'Referrals', path: '/dashboard/referrals' },
    { icon: Building2, label: 'Business Network', path: '/dashboard/businesses' },
  ];

  const advisorItems = [
    { icon: Smile, label: 'Dental Chart', path: '/dashboard/dental-chart' },
  ];

  const adminItems = [
    { icon: Settings, label: 'Admin Panel', path: '/admin' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-100 
        transform transition-transform duration-300 lg:translate-x-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">M</span>
              </div>
              <div>
                <span className="font-semibold text-primary text-sm">MED CONNECT</span>
                <span className="text-accent font-light ml-1 text-sm">ALBANIA</span>
              </div>
            </Link>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <span className="text-accent font-semibold">{user?.name?.[0]?.toUpperCase() || 'U'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Menu</p>
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-accent/10 text-accent'
                    : 'text-muted-foreground hover:bg-slate-50 hover:text-primary'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
                {item.isNew && <Badge className="ml-auto bg-accent text-white text-xs">NEW</Badge>}
              </Link>
            ))}

            {canUseDentalChart && (
              <div className="pt-4 mt-4 border-t border-slate-100">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Tools</p>
                {advisorItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? 'bg-accent/10 text-accent'
                        : 'text-muted-foreground hover:bg-slate-50 hover:text-primary'
                    }`}
                    data-testid="nav-dental-chart"
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                ))}
              </div>
            )}

            {isAdmin && (
              <>
                <div className="pt-4 mt-4 border-t border-slate-100">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Admin</p>
                  {adminItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive(item.path)
                          ? 'bg-accent/10 text-accent'
                          : 'text-muted-foreground hover:bg-slate-50 hover:text-primary'
                      }`}
                      data-testid="nav-admin"
                    >
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              </>
            )}
          </nav>

          {/* Bottom actions */}
          <div className="p-4 border-t border-slate-100 space-y-2">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-slate-50 hover:text-primary transition-colors"
            >
              <Home className="w-5 h-5" />
              Back to Home
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 transition-colors w-full"
              data-testid="sidebar-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Dashboard Header
const DashboardHeader = ({ setIsOpen }) => {
  const location = useLocation();
  
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Overview';
    if (path.includes('estimates')) return 'Cost Estimates';
    if (path.includes('referrals')) return 'Referrals';
    if (path.includes('businesses')) return 'Business Network';
    return 'Dashboard';
  };

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-slate-100 lg:pl-64">
      <div className="flex items-center justify-between px-4 md:px-8 h-16">
        <div className="flex items-center gap-4">
          <button
            className="lg:hidden p-2 -ml-2"
            onClick={() => setIsOpen(true)}
            data-testid="mobile-sidebar-toggle"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2 text-sm">
            <Link to="/dashboard" className="text-muted-foreground hover:text-primary">Dashboard</Link>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium">{getPageTitle()}</span>
          </div>
        </div>
      </div>
    </header>
  );
};

// Overview Page
const OverviewPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/referrals/stats`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, []);

  const copyReferralLink = async () => {
    const link = `${window.location.origin}/register?ref=${user?.referral_code}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Referral link copied to clipboard!');
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Referral link copied to clipboard!');
    }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      <div>
        <h1 className="text-2xl font-semibold text-primary">Welcome back, {user?.name?.split(' ')[0]}!</h1>
        <p className="text-muted-foreground">Here's an overview of your account.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-100 shadow-sm p-6"
        >
          <p className="text-sm text-muted-foreground mb-1">Total Referrals</p>
          <p className="text-3xl font-bold text-primary">{stats?.total_referrals || 0}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-slate-100 shadow-sm p-6"
        >
          <p className="text-sm text-muted-foreground mb-1">Pending</p>
          <p className="text-3xl font-bold text-yellow-500">{stats?.pending || 0}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border border-slate-100 shadow-sm p-6"
        >
          <p className="text-sm text-muted-foreground mb-1">Completed</p>
          <p className="text-3xl font-bold text-green-500">{stats?.completed || 0}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-slate-100 shadow-sm p-6"
        >
          <p className="text-sm text-muted-foreground mb-1">Total Commission</p>
          <p className="text-3xl font-bold text-accent">€{stats?.total_commission?.toFixed(2) || '0.00'}</p>
        </motion.div>
      </div>

      {/* Referral Link Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gradient-to-br from-primary to-primary/90 rounded-xl p-6 text-white"
      >
        <h3 className="text-lg font-semibold mb-2">Your Referral Link</h3>
        <p className="text-white/80 text-sm mb-4">Share this link to earn 5-10% commission on every successful referral.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 bg-white/10 rounded-lg px-4 py-3 text-sm font-mono truncate">
            {`${window.location.origin}/register?ref=${user?.referral_code || 'loading...'}`}
          </div>
          <Button
            variant="secondary"
            className="rounded-lg"
            onClick={copyReferralLink}
            data-testid="copy-referral-link-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy Link
          </Button>
        </div>
      </motion.div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Link
          to="/dashboard/estimates"
          className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 hover:shadow-md transition-shadow group"
        >
          <FileText className="w-8 h-8 text-accent mb-3" />
          <h3 className="font-semibold text-primary mb-1">Request Estimate</h3>
          <p className="text-sm text-muted-foreground">Get a free cost estimate for your treatment.</p>
          <span className="text-accent text-sm font-medium flex items-center gap-1 mt-3 group-hover:gap-2 transition-all">
            Get Started <ChevronRight className="w-4 h-4" />
          </span>
        </Link>

        <Link
          to="/dashboard/referrals"
          className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 hover:shadow-md transition-shadow group"
        >
          <Users className="w-8 h-8 text-accent mb-3" />
          <h3 className="font-semibold text-primary mb-1">View Referrals</h3>
          <p className="text-sm text-muted-foreground">Track your referrals and commissions.</p>
          <span className="text-accent text-sm font-medium flex items-center gap-1 mt-3 group-hover:gap-2 transition-all">
            View All <ChevronRight className="w-4 h-4" />
          </span>
        </Link>

        <Link
          to="/dashboard/businesses"
          className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 hover:shadow-md transition-shadow group"
        >
          <Building2 className="w-8 h-8 text-accent mb-3" />
          <h3 className="font-semibold text-primary mb-1">Partner Network</h3>
          <p className="text-sm text-muted-foreground">Explore our partner clinics and services.</p>
          <span className="text-accent text-sm font-medium flex items-center gap-1 mt-3 group-hover:gap-2 transition-all">
            Explore <ChevronRight className="w-4 h-4" />
          </span>
        </Link>
      </div>
    </div>
  );
};

// Main Dashboard Layout
export default function DashboardPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <Toaster position="top-center" richColors />
      <SidebarNav isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <DashboardHeader setIsOpen={setSidebarOpen} />
      
      <main className="lg:pl-64 pt-6 pb-12 px-4 md:px-8">
        <OverviewPage />
      </main>
    </div>
  );
}
